import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, QueryList, Renderer2, ViewChild, ViewChildren } from '@angular/core';
import { NgForm } from '@angular/forms';
import { animate, style, transition, trigger } from '@angular/animations';
import { Router } from '@angular/router';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { AsYouType } from 'libphonenumber-js';
import { Subscription } from 'rxjs';
import { ApiService } from '../../../../services/api.service';
import { ApiUrls } from '../../../../constants/api-urls';
import { Country } from '../../../../models/Country';
import { CurrencyService } from '../../../../services/currency.service';
import { TalkToSalesService } from '../../../../services/talk-to-sales.service';
import { readMarketingReferral } from '../../../../shared/marketing-referral.util';

type BillingTerm = 'monthly' | 'quarterly' | 'semiAnnual' | 'annual';

type BillingOption = {
  id: BillingTerm;
  label: string;
  discount?: string;
};

type PricingPlan = {
  name: string;
  monthlyPrice: number;
  quarterlyPrice: number;
  semiAnnualPrice: number;
  annualPrice: number;
  features: string[];
  description: string;
  buttonText: string;
  isPopular?: boolean;
  // Only a Monthly billing option exists for this plan (e.g. Startup) — the globally-selected
  // billing term is ignored for it and it always prices/checks out at its Monthly rate.
  monthlyOnly?: boolean;
  // populated from API
  tierCode?: string;
  quotaCalls?: number;
  planIds: Partial<Record<BillingTerm, string>>;
  totalPrices: Partial<Record<BillingTerm, number>>;
};

const INTERVAL_TO_TERM: Record<string, BillingTerm> = {
  MONTHLY: 'monthly',
  QUARTERLY: 'quarterly',
  SEMI_ANNUAL: 'semiAnnual',
  ANNUAL: 'annual',
};

const SELECTED_PLAN_KEY = 'icip_selected_plan';
const GERMANY_TIME_ZONE = 'Europe/Berlin';

type ContactFormData = {
  email: string;
  firstName: string;
  lastName: string;
  company: string;
  scenario: string;
  countryId: number | null;
  callVolume: string;
  phone: string;
  message: string;
  termsAccepted: boolean;
};

type Star = {
  top: string;
  left: string;
  sizePx: number;
  delayS: number;
  durationS: number;
  driftXPx: number;
  driftYPx: number;
  opacity: number;
};

type ConfettiParticle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  w: number;
  h: number;
  rot: number;
  vr: number;
  flip: number;
  vFlip: number;
  life: number;
  ttl: number;
  color: string;
  backColor: string;
  shape: 'rect' | 'circle';
};

const SCENARIOS = [
  'High-volume outbound calling (30,000+ calls/mo)',
  'Multi-location / franchise operations',
  'Call center or BPO environment',
  'Enterprise CRM integration',
  'Regulatory or compliance-driven branding',
  'Custom carrier agreements needed',
  'Other / Not sure yet',
];

const CALL_VOLUMES = [
  'Up to 15,000 calls/month',
  '15,000 – 30,000 calls/month',
  '30,000 – 50,000 calls/month',
  '50,000 – 100,000 calls/month',
  '100,000+ calls/month',
  'Not sure yet',
];

@Component({
  selector: 'app-home-pricing',
  standalone: false,
  templateUrl: './home-pricing.component.html',
  styleUrl: './home-pricing.component.scss',
  animations: [
    trigger('backdrop', [
      transition(':enter', [style({ opacity: 0 }), animate('200ms ease-out', style({ opacity: 1 }))]),
      transition(':leave', [animate('200ms ease-in', style({ opacity: 0 }))]),
    ]),
    trigger('modal', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(16px) scale(0.96)' }),
        animate('250ms cubic-bezier(0.2, 0.8, 0.2, 1)', style({ opacity: 1, transform: 'translateY(0) scale(1)' })),
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ opacity: 0, transform: 'translateY(16px) scale(0.96)' })),
      ]),
    ]),
  ],
})
export class HomePricingComponent implements OnInit, AfterViewInit, OnDestroy {
  billingTerm: BillingTerm = 'monthly';

  billingOptions: BillingOption[] = [
    { id: 'monthly', label: 'Monthly' },
    { id: 'quarterly', label: '3 Months', discount: 'Save 10%' },
    { id: 'semiAnnual', label: '6 Months', discount: 'Save 15%' },
    { id: 'annual', label: 'Annual', discount: 'Save 25%' },
  ];

  currencySymbol = '€';
  currentCurrency = 'EUR';

  plans: PricingPlan[] = [
    {
      name: 'Startup',
      monthlyPrice: 89,
      quarterlyPrice: 89,
      semiAnnualPrice: 89,
      annualPrice: 89,
      quotaCalls: 500,
      features: [
        'Branded calling for 500 calls per period',
        'Custom Display Name',
        'Real-time analytics dashboard',
        'Email support',
        'Portal self-service management',
      ],
      description: 'The simplest way to start with branded calling.',
      buttonText: 'Get Started',
      monthlyOnly: true,
      planIds: {},
      totalPrices: {},
    },
    {
      name: 'Starter',
      monthlyPrice: 350,
      quarterlyPrice: 315,
      semiAnnualPrice: 297.5,
      annualPrice: 262.5,
      quotaCalls: 2500,
      features: [
        'Up to 2,500 Branded Calls/mo',
        'First Orion INFORM Call Display',
        'Caller Name Display',
        'Basic analytics dashboard',
        'Standard email support',
      ],
      description: 'Perfect for small businesses starting with branded calling.',
      buttonText: 'Get Started',
      planIds: {},
      totalPrices: {},
    },
    {
      name: 'Growth',
      monthlyPrice: 900,
      quarterlyPrice: 810,
      semiAnnualPrice: 765,
      annualPrice: 675,
      quotaCalls: 7500,
      features: [
        'Branded calling for 7,500 calls per period',
        'Custom Display Name',
        'Advanced analytics & reporting',
        'Priority email & chat support',
        'Portal self-service management',
        'API access',
      ],
      description: 'Ideal for growing teams with higher call volumes.',
      buttonText: 'Get Started',
      isPopular: true,
      planIds: {},
      totalPrices: {},
    },
    {
      name: 'Business',
      monthlyPrice: 1500,
      quarterlyPrice: 1350,
      semiAnnualPrice: 1275,
      annualPrice: 1125,
      quotaCalls: 15000,
      features: [
        'Branded calling for 15,000 calls per period',
        'Custom Display Name',
        'Enterprise analytics & reporting',
        'Dedicated account support',
        'Full API access',
        'Custom branding options',
        'Priority network distribution',
      ],
      description: 'For organizations maximizing their call connection rates.',
      buttonText: 'Get Started',
      planIds: {},
      totalPrices: {},
    },
  ];

  enterpriseFeatures: string[] = [
    'Unlimited branded call volume',
    'Custom Display Name',
    'Custom carrier & API integrations',
    'Advanced analytics & SLA reporting',
    'Dedicated European account manager',
    'Priority 24/7 support',
    'Custom compliance & regulatory setup',
    'Flexible billing & contract terms',
  ];

  stars: Star[] = this.buildStars(80);

  pillStyle: { width: string; transform: string } = { width: '0px', transform: 'translateX(0px)' };
  salesModalOpen = false;
  germanyEligibilityModalOpen = false;
  showGermanyInlineNotice = false;
  // The plan a visitor clicked "Start Now"/"Get Started" on — held while the Germany
  // eligibility modal is open, then handed to goToReviewSetup() once they confirm.
  private pendingPlanSelection: PricingPlan | null = null;
  submitted = false;
  formSubmitAttempted = false;
  isSubmittingSalesRequest = false;
  salesRequestError: string | null = null;

  scenarios = SCENARIOS;
  callVolumes = CALL_VOLUMES;
  countries: Country[] = [];

  // Phone Number's own country-code selector (flag + dial code + search), independent of the
  // "Country" field above — mirrors add-verify-business.component.ts's phone country-dropdown.
  selectedCountryCode: Country | null = null;
  isCountryDropdownOpen = false;
  countrySearchTerm = '';

  get filteredCountries(): Country[] {
    const term = this.countrySearchTerm.trim().toLowerCase();
    if (!term) {
      return this.countries;
    }
    return this.countries.filter((c: Country) => {
      const haystack = JSON.stringify(c ?? {}).toLowerCase();
      return haystack.includes(term);
    });
  }

  form: ContactFormData = {
    email: '',
    firstName: '',
    lastName: '',
    company: '',
    scenario: '',
    countryId: null,
    callVolume: '',
    phone: '',
    message: '',
    termsAccepted: false,
  };

  animatedPrices: Record<string, number> = {};

  @ViewChildren('billingBtn') billingButtons!: QueryList<ElementRef<HTMLButtonElement>>;
  @ViewChild('billingEl') billingEl?: ElementRef<HTMLDivElement>;
  @ViewChild('burstLayer') burstLayer?: ElementRef<HTMLDivElement>;
  @ViewChild('starfield') starfield?: ElementRef<HTMLDivElement>;
  @ViewChild('confettiCanvas') confettiCanvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('phoneCountryDropdown') phoneCountryDropdown?: ElementRef<HTMLElement>;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isCountryDropdownOpen) {
      return;
    }
    const target = event.target as HTMLElement | null;
    const root = this.phoneCountryDropdown?.nativeElement;
    if (target && root && !root.contains(target)) {
      this.isCountryDropdownOpen = false;
    }
  }

  private priceRafIds: number[] = [];
  private unlistenMouseMove: (() => void) | null = null;
  private unlistenMouseLeave: (() => void) | null = null;
  private unlistenBillingResize: (() => void) | null = null;
  private unlistenBillingOrientation: (() => void) | null = null;
  private unlistenBillingScroll: (() => void) | null = null;

  private confettiParticles: ConfettiParticle[] = [];
  private confettiRafId: number | null = null;
  private lastConfettiTs = 0;
  private confettiResizeObserver: ResizeObserver | null = null;
  private unlistenConfettiResize: (() => void) | null = null;
  private resizeConfettiCanvas: (() => void) | null = null;

  constructor(
    private renderer: Renderer2,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private apiService: ApiService,
    private recaptchaV3Service: ReCaptchaV3Service,
    private currencyService: CurrencyService,
    private talkToSalesService: TalkToSalesService
  ) {
    for (const plan of this.plans) {
      this.animatedPrices[plan.name] = plan.monthlyPrice;
    }
  }

  private currencySub?: Subscription;
  private talkToSalesSub?: Subscription;

  ngOnInit(): void {
    this.talkToSalesSub = this.talkToSalesService.openRequests$.subscribe(() => this.openSalesModal());

    // Marketing deep-link: open the Talk to Sales popup automatically when the visitor lands with
    // ?cta=contact (e.g. https://portal/?cta=contact). Done here (not at app bootstrap) because
    // this component owns the modal and has just subscribed — avoids a Subject timing race.
    this.maybeOpenSalesModalFromCta();

    this.apiService.get(ApiUrls.Lookup.GET_COUNTRIES, undefined, { skipGlobalLoader: true }).subscribe({
      next: (res) => {
        this.countries = (res?.data?.countries ?? []) as Country[];
      },
      error: (err) => {
        console.error('Error loading countries', err);
      },
    });

    // BehaviorSubject emits the current value synchronously on subscribe, so this both fetches
    // the initial plans (in the navbar toggle's default currency, EUR) and re-fetches whenever
    // the visitor switches currency from the header toggle.
    this.currencySub = this.currencyService.currency$.subscribe((currency) => {
      this.fetchPlans(currency.code);
    });

    this.checkGermanyInlineNotice();
  }

  // Self-serve plans are Germany-only for now, detected from the visitor's browser timezone.
  // Non-German visitors get an inline notice, always visible above the pricing grid. The
  // eligibility modal itself is no longer triggered by this timezone check — it's gated on
  // clicking a plan's CTA instead, see onPlanCtaClick().
  private checkGermanyInlineNotice(): void {
    const timeZone = this.getTimeZone();
    if (timeZone === null || timeZone === GERMANY_TIME_ZONE) {
      return;
    }

    this.showGermanyInlineNotice = true;
  }

  private getTimeZone(): string | null {
    if (typeof Intl === 'undefined') {
      return null;
    }
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
    } catch {
      return null;
    }
  }

  // Entry point for a plan card's CTA button — every plan selection must be confirmed via the
  // Germany eligibility modal before proceeding to registration.
  onPlanCtaClick(plan: PricingPlan): void {
    this.pendingPlanSelection = plan;
    this.germanyEligibilityModalOpen = true;
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden';
    }
  }

  // "I'm in Germany — continue" — proceeds to registration with the plan that was pending.
  confirmGermanyEligibility(): void {
    const plan = this.pendingPlanSelection;
    this.closeGermanyEligibilityModal();
    if (plan) {
      this.goToReviewSetup(plan);
    }
  }

  closeGermanyEligibilityModal(): void {
    this.germanyEligibilityModalOpen = false;
    this.pendingPlanSelection = null;
    if (typeof document !== 'undefined' && !this.salesModalOpen) {
      document.body.style.overflow = '';
    }
  }

  talkToSalesFromGermanyModal(): void {
    this.closeGermanyEligibilityModal();
    this.openSalesModal();
  }

  private fetchPlans(currency: string): void {
    this.apiService.get(ApiUrls.SelfServe.GET_PLANS, { currency }, { skipGlobalLoader: true }).subscribe({
      next: (res) => {
        if (!res?.data?.items?.length) return;

        const tiers: any[] = res.data.items;
        this.currencySymbol = tiers[0]?.currencySymbol ?? this.currencyService.currency.symbol;
        this.currentCurrency = tiers[0]?.currency ?? currency;

        this.plans = tiers.map((tier: any) => {
          const prices: Partial<Record<BillingTerm, number>> = {};
          const ids: Partial<Record<BillingTerm, string>> = {};
          const totals: Partial<Record<BillingTerm, number>> = {};

          for (const opt of (tier.billingOptions ?? [])) {
            const term = INTERVAL_TO_TERM[opt.billingInterval as string];
            if (!term) continue;
            prices[term] = (opt.pricePerMonthCents ?? 0) / 100;
            ids[term] = opt.planId;
            totals[term] = (opt.totalPriceCents ?? 0) / 100;
          }

          const isStarter = (tier.tierCode as string) === 'STARTER';
          // Only a Monthly billingOption came back for this tier — no Quarterly/Semi-Annual/
          // Annual variant is sellable, so the plan must always price/checkout at Monthly
          // regardless of which billing term is globally selected (see getPlanPrice()).
          const monthlyOnly = !ids['quarterly'] && !ids['semiAnnual'] && !ids['annual'];
          return {
            name: tier.name,
            tierCode: tier.tierCode,
            description: tier.description ?? '',
            isPopular: tier.popular ?? false,
            monthlyOnly,
            quotaCalls: tier.quotaCalls,
            features: tier.features ?? [],
            buttonText: isStarter ? 'Get Started' : 'Get Started',
            monthlyPrice: prices['monthly'] ?? 0,
            quarterlyPrice: prices['quarterly'] ?? 0,
            semiAnnualPrice: prices['semiAnnual'] ?? 0,
            annualPrice: prices['annual'] ?? 0,
            planIds: ids,
            totalPrices: totals,
          } as PricingPlan;
        });

        // Reset animated prices to the currently-selected billing term (not always monthly) so
        // switching currency doesn't visually snap back to monthly pricing when e.g. Annual is
        // already selected.
        for (const plan of this.plans) {
          this.animatedPrices[plan.name] = this.getPlanPrice(plan, this.billingTerm);
        }

        this.cdr.detectChanges();
      },
      error: () => {
        // Silently keep the previously loaded (or hardcoded fallback) plans already in place
      },
    });
  }

  ngAfterViewInit(): void {
    Promise.resolve().then(() => {
      this.updatePill();
      this.cdr.detectChanges();
    });

    // Re-measure once web fonts finish swapping in — the pill's width/transform are captured
    // from the button's rendered layout, so if Inter loads after the first measurement above,
    // the fallback-font button widths leave the pill visibly misaligned with the real text.
    const fonts = (typeof document !== 'undefined' ? (document as any).fonts : null) as
      | { ready?: Promise<unknown> }
      | null;
    fonts?.ready?.then(() => {
      this.updatePill();
      this.cdr.detectChanges();
    });

    this.initConfettiCanvas();

    this.billingButtons.changes.subscribe(() => {
      Promise.resolve().then(() => {
        this.updatePill();
        this.cdr.detectChanges();
      });
    });

    const billing = this.billingEl?.nativeElement;
    if (billing) {
      this.unlistenBillingScroll = this.renderer.listen(billing, 'scroll', () => {
        this.updatePill();
      });
    }

    this.unlistenBillingResize = this.renderer.listen('window', 'resize', () => {
      this.updatePill();
    });

    this.unlistenBillingOrientation = this.renderer.listen('window', 'orientationchange', () => {
      setTimeout(() => this.updatePill(), 0);
    });

    const starfieldEl = this.starfield?.nativeElement;
    if (starfieldEl) {
      this.zone.runOutsideAngular(() => {
        const parent = starfieldEl.parentElement;
        if (!parent) return;

        this.unlistenMouseMove = this.renderer.listen(parent, 'mousemove', (event: MouseEvent) => {
          const rect = parent.getBoundingClientRect();
          const dx = (event.clientX - (rect.left + rect.width / 2)) / rect.width;
          const dy = (event.clientY - (rect.top + rect.height / 2)) / rect.height;

          const x = Math.max(-1, Math.min(1, dx)) * 18;
          const y = Math.max(-1, Math.min(1, dy)) * 18;

          starfieldEl.style.transform = `translate3d(${x}px, ${y}px, 0)`;
        });

        this.unlistenMouseLeave = this.renderer.listen(parent, 'mouseleave', () => {
          starfieldEl.style.transform = 'translate3d(0px, 0px, 0)';
        });
      });
    }
  }

  ngOnDestroy(): void {
    this.currencySub?.unsubscribe();
    this.talkToSalesSub?.unsubscribe();

    this.priceRafIds.forEach((id) => cancelAnimationFrame(id));
    this.priceRafIds = [];

    this.unlistenMouseMove?.();
    this.unlistenMouseMove = null;

    this.unlistenMouseLeave?.();
    this.unlistenMouseLeave = null;

    this.unlistenBillingResize?.();
    this.unlistenBillingResize = null;

    this.unlistenBillingOrientation?.();
    this.unlistenBillingOrientation = null;

    this.unlistenBillingScroll?.();
    this.unlistenBillingScroll = null;

    if (this.confettiRafId != null) {
      cancelAnimationFrame(this.confettiRafId);
      this.confettiRafId = null;
    }

    this.confettiResizeObserver?.disconnect();
    this.confettiResizeObserver = null;

    this.unlistenConfettiResize?.();
    this.unlistenConfettiResize = null;

    this.resizeConfettiCanvas = null;

    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }
  }

  trackByPlanName(_idx: number, plan: PricingPlan): string {
    return plan.name;
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackByString(_idx: number, value: string): string {
    return value;
  }

  formatPlanCurrency(plan: PricingPlan, value: number): string {
    const target = this.getPlanPrice(plan, this.billingTerm);
    const showDecimals = !Number.isInteger(target);
    const decimals = showDecimals ? 2 : 0;
    const safe = Number.isFinite(value) ? value : target;
    const rounded = decimals === 0 ? Math.round(safe) : Math.round(safe * 100) / 100;

    try {
      const formatted = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      }).format(rounded);
      return `${this.currencySymbol}${formatted}`;
    } catch {
      return `${this.currencySymbol}${rounded.toFixed(decimals)}`;
    }
  }

  getBillingLabel(): string {
    if (this.billingTerm === 'quarterly') return 'Billed Every 3 Months';
    if (this.billingTerm === 'semiAnnual') return 'Billed Every 6 Months';
    if (this.billingTerm === 'annual') return 'Billed Annually';
    return '';
  }

  // Resolves which billing term a given plan actually prices/checks out at — the globally
  // selected term, unless the plan is Monthly-only (e.g. Startup), in which case it always
  // stays on 'monthly' no matter what's selected elsewhere on the page.
  effectiveTerm(plan: PricingPlan, term: BillingTerm): BillingTerm {
    return plan.monthlyOnly ? 'monthly' : term;
  }

  getPlanPrice(plan: PricingPlan, term: BillingTerm): number {
    const effective = this.effectiveTerm(plan, term);
    if (effective === 'monthly') return plan.monthlyPrice;
    if (effective === 'quarterly') return plan.quarterlyPrice;
    if (effective === 'semiAnnual') return plan.semiAnnualPrice;
    return plan.annualPrice;
  }

  selectBilling(term: BillingTerm, event: MouseEvent): void {
    if (this.billingTerm === term) return;

    const button = event.currentTarget as HTMLButtonElement | null;

    this.billingTerm = term;
    requestAnimationFrame(() => this.updatePill());

    if (term !== 'monthly' && button) {
      this.triggerConfetti(button);
    }

    for (const plan of this.plans) {
      const from = this.animatedPrices[plan.name] ?? this.getPlanPrice(plan, 'monthly');
      const to = this.getPlanPrice(plan, term);
      this.animateNumber(plan.name, from, to, 520);
    }
  }

  goToReviewSetup(plan: PricingPlan): void {
    const term = this.effectiveTerm(plan, this.billingTerm);
    const planId = plan.planIds[term];
    const totalPrice = plan.totalPrices[term];

    sessionStorage.setItem(
      SELECTED_PLAN_KEY,
      JSON.stringify({
        planId: planId ?? null,
        tierCode: plan.tierCode ?? '',
        planName: plan.name,
        billingTerm: term,
        pricePerMonth: this.getPlanPrice(plan, term),
        totalPrice: totalPrice ?? this.getPlanPrice(plan, term),
        currency: this.currentCurrency,
        currencySymbol: this.currencySymbol,
        quotaCalls: plan.quotaCalls ?? null,
        features: plan.features,
      })
    );

    this.router.navigate(['/register']);
  }

  /**
   * Opens the Talk to Sales popup when the landing URL carries ?cta=contact (case-insensitive).
   * Marketing deep-link entry point — no-op otherwise. Reads window.location directly so it works
   * regardless of router state at init.
   */
  private maybeOpenSalesModalFromCta(): void {
    try {
      if (typeof window === 'undefined') return;
      const cta = new URLSearchParams(window.location.search).get('cta');
      if (cta && cta.trim().toLowerCase() === 'contact') {
        this.openSalesModal();
      }
    } catch {
      /* no-op — non-browser context or malformed URL */
    }
  }

  openSalesModal(): void {
    this.salesModalOpen = true;
    if (typeof document !== 'undefined') {
      document.body.style.overflow = 'hidden';
    }

    // Warm-up token fetch (mirrors register.component.ts's ngOnInit prefetch) so the real
    // submit-time execute() call resolves faster — the result itself is discarded here.
    this.recaptchaV3Service.execute('talk_to_sales').subscribe({ next: () => {}, error: () => {} });
  }

  closeSalesModal(): void {
    this.salesModalOpen = false;
    if (typeof document !== 'undefined') {
      document.body.style.overflow = '';
    }

    setTimeout(() => {
      this.submitted = false;
      this.formSubmitAttempted = false;
      this.salesRequestError = null;
      this.selectedCountryCode = null;
      this.isCountryDropdownOpen = false;
      this.countrySearchTerm = '';
      this.form = {
        email: '',
        firstName: '',
        lastName: '',
        company: '',
        scenario: '',
        countryId: null,
        callVolume: '',
        phone: '',
        message: '',
        termsAccepted: false,
      };
    }, 300);
  }

  toggleCountryDropdown(): void {
    this.isCountryDropdownOpen = !this.isCountryDropdownOpen;
  }

  onCountrySearch(term: string): void {
    this.countrySearchTerm = term ?? '';
  }

  selectCountryCode(country: Country): void {
    this.selectedCountryCode = country;
    this.isCountryDropdownOpen = false;
    this.countrySearchTerm = '';
  }

  // Live-formats the national number as the user types, using the selected country's dial
  // code — mirrors add-verify-business.component.ts's onBusinessPhoneInput.
  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) return;
    const rawValue = input.value || '';
    const digitsOnly = rawValue.replace(/\D/g, '');
    const callingCodeRaw = this.selectedCountryCode?.telephoneCode || null;
    const callingCode = callingCodeRaw ? callingCodeRaw.replace('+', '') : undefined;
    const formatter = callingCode ? new AsYouType({ defaultCallingCode: callingCode }) : new AsYouType();
    const formatted = formatter.input(digitsOnly);
    input.value = formatted;
    this.form.phone = formatted;
  }

  // Combines the selected country's dial code with the typed national number into a single
  // E.164 string before submit — mirrors add-verify-business.component.ts's getE164Phone.
  private getE164Phone(): string {
    const dialDigits = (this.selectedCountryCode?.telephoneCode || '').replace(/\D/g, '');
    const localDigits = (this.form.phone || '').replace(/\D/g, '');

    const localClean = dialDigits && localDigits.startsWith(dialDigits)
      ? localDigits.substring(dialDigits.length)
      : localDigits;

    return dialDigits ? `+${dialDigits}${localClean}` : localClean;
  }

  async submitSalesRequest(event: Event, salesForm: NgForm): Promise<void> {
    event.preventDefault();
    this.formSubmitAttempted = true;
    this.salesRequestError = null;

    if (salesForm.invalid) {
      return;
    }

    this.isSubmittingSalesRequest = true;

    let captchaToken: string;
    try {
      captchaToken = await this.executeRecaptcha('talk_to_sales');
    } catch {
      this.isSubmittingSalesRequest = false;
      this.salesRequestError = 'reCAPTCHA verification failed. Please try again.';
      return;
    }

    const payload = {
      email: this.form.email,
      firstName: this.form.firstName,
      lastName: this.form.lastName,
      company: this.form.company,
      scenario: this.form.scenario,
      countryId: this.form.countryId,
      callVolume: this.form.callVolume,
      phone: this.getE164Phone(),
      message: this.form.message,
      // Marketing attribution — full landing URL (incl. UTM) captured at app bootstrap; omitted
      // (null server-side) when the lead arrived directly.
      marketingReferral: readMarketingReferral(),
      captchaToken,
    };

    this.apiService.post(ApiUrls.Public.SALES_CONTACT, payload).subscribe({
      next: () => {
        this.isSubmittingSalesRequest = false;
        this.submitted = true;
      },
      error: (err) => {
        this.isSubmittingSalesRequest = false;
        // Field-level errors (e.g. "A request has already been submitted for this email
        // address.") are more useful than the generic "Validation failed" summary message.
        this.salesRequestError =
          err?.error?.errors?.[0]?.message ??
          err?.error?.message ??
          'Something went wrong. Please try again.';
      },
    });
  }

  private executeRecaptcha(action: string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.recaptchaV3Service.execute(action).subscribe({
        next: (token) => {
          if (!token) {
            reject(new Error('Empty reCAPTCHA token'));
            return;
          }
          resolve(token);
        },
        error: (err) => reject(err),
      });
    });
  }

  private animateNumber(key: string, from: number, to: number, durMs: number): void {
    const start = performance.now();
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / durMs);
      const eased = easeOutCubic(t);
      this.animatedPrices[key] = from + (to - from) * eased;

      if (t < 1) {
        this.priceRafIds.push(requestAnimationFrame(tick));
      } else {
        this.animatedPrices[key] = to;
      }
    };

    this.priceRafIds.push(requestAnimationFrame(tick));
  }

  private updatePill(): void {
    const btn = this.getActiveBillingButton();
    if (!btn) {
      this.pillStyle = { width: '0px', transform: 'translateX(0px)' };
      return;
    }

    const container = this.billingEl?.nativeElement ?? (btn.parentElement as HTMLDivElement | null);
    if (!container) {
      this.pillStyle = { width: `${btn.offsetWidth}px`, transform: `translateX(${btn.offsetLeft}px)` };
      return;
    }

    const btnRect = btn.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const x = btnRect.left - containerRect.left;

    this.pillStyle = {
      width: `${btnRect.width}px`,
      transform: `translateX(${x}px)`,
    };
  }

  private getActiveBillingButton(): HTMLButtonElement | null {
    const btnRef = this.billingButtons?.find((b) => (b.nativeElement.dataset['term'] ?? '') === this.billingTerm);
    return btnRef?.nativeElement ?? null;
  }

  private triggerBurst(button: HTMLButtonElement): void {
    const layer = this.burstLayer?.nativeElement;
    if (!layer) return;

    const layerRect = layer.getBoundingClientRect();
    const btnRect = button.getBoundingClientRect();

    const x = btnRect.left - layerRect.left + btnRect.width / 2;
    const y = btnRect.top - layerRect.top + btnRect.height / 2;

    const burst = this.renderer.createElement('div') as HTMLDivElement;
    this.renderer.addClass(burst, 'icip-pricing__burst');
    this.renderer.setStyle(burst, 'left', `${x}px`);
    this.renderer.setStyle(burst, 'top', `${y}px`);

    const particleCount = 14;

    for (let i = 0; i < particleCount; i++) {
      const p = this.renderer.createElement('span') as HTMLSpanElement;
      this.renderer.addClass(p, 'icip-pricing__particle');

      const angle = (Math.PI * 2 * i) / particleCount + (Math.random() * 0.35 - 0.175);
      const dist = 18 + Math.random() * 18;
      const dx = Math.cos(angle) * dist;
      const dy = Math.sin(angle) * dist;
      const s = 0.75 + Math.random() * 0.55;

      this.renderer.setStyle(p, '--dx', `${dx}px`);
      this.renderer.setStyle(p, '--dy', `${dy}px`);
      this.renderer.setStyle(p, '--s', `${s}`);

      this.renderer.appendChild(burst, p);
    }

    this.renderer.appendChild(layer, burst);

    setTimeout(() => {
      try {
        this.renderer.removeChild(layer, burst);
      } catch {
        // ignore
      }
    }, 850);
  }

  private initConfettiCanvas(): void {
    const canvas = this.confettiCanvas?.nativeElement;
    if (!canvas) return;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = (globalThis as any)?.devicePixelRatio || 1;
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }
    };

    this.resizeConfettiCanvas = resize;

    resize();

    const RO = (globalThis as any)?.ResizeObserver as (typeof ResizeObserver | undefined);
    if (RO) {
      this.confettiResizeObserver = new RO(() => resize());
      this.confettiResizeObserver.observe(canvas);
      return;
    }

    this.unlistenConfettiResize = this.renderer.listen('window', 'resize', resize);
  }

  private triggerConfetti(button: HTMLButtonElement): void {
    const canvas = this.confettiCanvas?.nativeElement;
    if (!canvas) return;

    this.resizeConfettiCanvas?.();

    const canvasRect = canvas.getBoundingClientRect();
    const btnRect = button.getBoundingClientRect();

    const originX = btnRect.left + btnRect.width / 2 - canvasRect.left;
    const originY = btnRect.top + btnRect.height / 2 - canvasRect.top;

    const colors = ['#071D49', '#E5E7EB', '#F9FAFB'];

    const count = 50;
    for (let i = 0; i < count; i++) {
      const a = (-Math.PI / 2) + (Math.random() * 1.05 - 0.525);
      const speed = 580 + Math.random() * 280;

      const vx = Math.cos(a) * speed;
      const vy = Math.sin(a) * speed * 1.18;

      const size = 4 + Math.random() * 4.5;
      const isCircle = Math.random() < 0.26;

      this.confettiParticles.push({
        x: originX,
        y: originY,
        vx,
        vy,
        r: Math.random() * Math.PI * 2,
        w: size,
        h: size * (0.6 + Math.random() * 0.7),
        rot: Math.random() * Math.PI * 2,
        vr: (Math.random() * 10 - 5),
        flip: Math.random() * Math.PI * 2,
        vFlip: (8 + Math.random() * 12) * (Math.random() < 0.5 ? -1 : 1),
        life: 0,
        ttl: 1.75 + Math.random() * 0.75,
        color: colors[Math.floor(Math.random() * colors.length)]!,
        backColor: this.mixWithWhite(colors[Math.floor(Math.random() * colors.length)]!, 0.78),
        shape: isCircle ? 'circle' : 'rect',
      });
    }

    if (this.confettiParticles.length > 380) {
      this.confettiParticles = this.confettiParticles.slice(-380);
    }

    if (this.confettiRafId == null) {
      this.lastConfettiTs = performance.now();
      this.zone.runOutsideAngular(() => {
        this.confettiRafId = requestAnimationFrame(this.tickConfetti);
      });
    }
  }

  private tickConfetti = (ts: number): void => {
    const canvas = this.confettiCanvas?.nativeElement;
    if (!canvas) {
      this.confettiRafId = null;
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      this.confettiRafId = null;
      return;
    }

    const dt = Math.min(0.034, Math.max(0.001, (ts - this.lastConfettiTs) / 1000));
    this.lastConfettiTs = ts;

    const gravity = 780;
    const drag = 0.984;

    const rect = canvas.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;

    const dpr = (globalThis as any)?.devicePixelRatio || 1;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const next: ConfettiParticle[] = [];

    for (const p of this.confettiParticles) {
      p.life += dt;
      if (p.life >= p.ttl) continue;

      p.vx *= Math.pow(drag, dt * 60);
      p.vy = p.vy * Math.pow(drag, dt * 60) + gravity * dt;

      p.x += p.vx * dt;
      p.y += p.vy * dt;

      p.rot += p.vr * dt;
      p.flip += p.vFlip * dt;

      const fade = 1 - Math.pow(p.life / p.ttl, 2.15);
      const alpha = Math.max(0, Math.min(1, fade));

      if (p.y < -180 || p.x < -60 || p.x > w + 60 || p.y > h + 110) {
        continue;
      }

      ctx.save();
      const flipCos = p.shape === 'rect' ? Math.cos(p.flip) : 1;
      const flipScale = p.shape === 'rect' ? Math.max(0.12, Math.abs(flipCos)) : 1;
      ctx.globalAlpha = alpha * (0.55 + 0.45 * flipScale);
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = flipCos >= 0 ? p.color : p.backColor;

      if (p.shape === 'circle') {
        ctx.beginPath();
        ctx.arc(0, 0, p.w * 0.5, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.scale(1, flipScale);
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
      }

      ctx.restore();
      next.push(p);
    }

    this.confettiParticles = next;

    if (this.confettiParticles.length > 0) {
      this.confettiRafId = requestAnimationFrame(this.tickConfetti);
    } else {
      this.confettiRafId = null;
      ctx.clearRect(0, 0, w, h);
    }
  };

  private buildStars(count: number): Star[] {
    const stars: Star[] = [];

    for (let i = 0; i < count; i++) {
      const size = 1 + Math.random() * 2;
      const drift = 8 + Math.random() * 14;
      const angle = Math.random() * Math.PI * 2;

      stars.push({
        top: `${Math.random() * 100}%`,
        left: `${Math.random() * 100}%`,
        sizePx: size,
        delayS: Math.random() * 4.5,
        durationS: 2.2 + Math.random() * 3.2,
        driftXPx: Math.cos(angle) * drift,
        driftYPx: Math.sin(angle) * drift,
        opacity: 0.12 + Math.random() * 0.22,
      });
    }

    return stars;
  }

  private mixWithWhite(hex: string, t: number): string {
    const raw = hex.replace('#', '').trim();
    if (raw.length !== 6) return hex;
    const r = parseInt(raw.slice(0, 2), 16);
    const g = parseInt(raw.slice(2, 4), 16);
    const b = parseInt(raw.slice(4, 6), 16);
    const tt = Math.max(0, Math.min(1, t));
    const rr = Math.round(r + (255 - r) * tt);
    const gg = Math.round(g + (255 - g) * tt);
    const bb = Math.round(b + (255 - b) * tt);
    return `rgb(${rr}, ${gg}, ${bb})`;
  }
}
